import React from "react";
import {
  StyledSelect,
  StyledTextField,
} from "../../../customComponents/styled/styledInputs";
import { useFormik } from "formik";
import * as yup from "yup";
import {
  Box,
  FormControl,
  FormHelperText,
  Grid,
  MenuItem,
  Typography,
} from "@mui/material";
import { StyledLoadingButton } from "../../../customComponents/styled/styledButtons";
import { useUpdateGradeMutation } from "./gradesApiSlice";
import toast from "../../../../helpers/toast";

const validationSchema = yup.object({
  name: yup.string().required("Grade is required"),
  min_score: yup
    .number("must be a number")
    .nullable()
    .transform((value, original) => (original === "" || original === null ? null : value))
    .max(100, "max is 100")
    .min(0, "min is 0")
    .when("type", {
      is: "comment",
      then: (s) => s.notRequired(),
      otherwise: (s) => s.required("Required"),
    }),
  max_score: yup
    .number()
    .nullable()
    .transform((value, original) => (original === "" || original === null ? null : value))
    .max(100, "max is 100")
    .min(0, "min is 0")
    .when("type", {
      is: "comment",
      then: (s) => s.notRequired(),
      otherwise: (s) => s.required("Required"),
    }),
  type: yup.string().required("Required"),
  description: yup.string().required("Required"),
  subsection_id: yup.string().required("Required"),
});

function EditGradeForm({ item, subsections, setItem }) {
  const [updateGrade, { isLoading: updateGradeLoading }] =
    useUpdateGradeMutation();

  const formik = useFormik({
    initialValues: {
      id: item.id,
      name: item.name,
      description: item.description,
      type: item.type,
      max_score: item.max_score,
      min_score: item.min_score,
      subsection_id: item.subsection_id,
    },
    enableReinitialize: true,
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      try {
        const response = await updateGrade(values).unwrap();
        if (response.id) {
          toast.success("Grade updated successfully");
          formik.resetForm();
          setItem(null);
        } else {
          toast.error("Grade not updated");
        }
      } catch (error) {
        toast.error(error?.message || "Something went wrong");
      }
    },
  });
  return (
    <>
      <Box className="paperHeader">
        <Typography variant="formHeading">Edit Grade</Typography>
      </Box>
      <Box className="paperBody">
        <form onSubmit={formik.handleSubmit}>
          <Grid container columnSpacing={1} rowSpacing={2}>
            <Grid item xs={12}>
              <FormControl error fullWidth>
                <Typography variant="formSubHeading">Grade</Typography>
                <StyledTextField
                  variant="outlined"
                  id="name"
                  name="name"
                  value={formik.values.name}
                  onChange={formik.handleChange}
                  error={formik.touched.name && Boolean(formik.errors.name)}
                />
                <FormHelperText variant="error">
                  {formik.touched.name && formik.errors.name}
                </FormHelperText>
              </FormControl>
            </Grid>

            {/* {second Row begin} */}

            <Grid item xs={12} lg={6}>
              <FormControl error fullWidth>
                <Typography variant="formSubHeading">Minimum score</Typography>
                <StyledTextField
                  variant="outlined"
                  id="min_score"
                  name="min_score"
                  value={formik.values.min_score}
                  onChange={formik.handleChange}
                  error={
                    formik.touched.min_score && Boolean(formik.errors.min_score)
                  }
                />
                <FormHelperText variant="error">
                  {formik.touched.min_score && formik.errors.min_score}
                </FormHelperText>
              </FormControl>
            </Grid>
            <Grid item xs={12} lg={6}>
              <FormControl error fullWidth>
                <Typography variant="formSubHeading">Maximum Score</Typography>
                <StyledTextField
                  variant="outlined"
                  id="max_score"
                  name="max_score"
                  value={formik.values.max_score}
                  onChange={formik.handleChange}
                  error={
                    formik.touched.max_score && Boolean(formik.errors.max_score)
                  }
                />
                <FormHelperText variant="error">
                  {formik.touched.max_score && formik.errors.max_score}
                </FormHelperText>
              </FormControl>
            </Grid>

            {/* end of second row  */}

            <Grid item xs={12}>
              <FormControl error fullWidth>
                <Typography variant="formSubHeading">Description</Typography>
                <StyledTextField
                  variant="outlined"
                  id="description"
                  name="description"
                  value={formik.values.description}
                  onChange={formik.handleChange}
                  error={
                    formik.touched.description &&
                    Boolean(formik.errors.description)
                  }
                />
                <FormHelperText variant="error">
                  {formik.touched.description && formik.errors.description}
                </FormHelperText>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl error fullWidth>
                <Typography variant="formSubHeading">type</Typography>
                <StyledSelect
                  displayEmpty
                  variant="outlined"
                  id="type"
                  name="type"
                  value={formik.values.type}
                  onChange={formik.handleChange}
                  error={formik.touched.type && Boolean(formik.errors.type)}
                >
                  <MenuItem value="all" disabled>
                    <em>select type</em>
                  </MenuItem>
                  <MenuItem value="score">score</MenuItem>
                  <MenuItem value="comment">comment</MenuItem>
                </StyledSelect>
                <FormHelperText variant="error">
                  {formik.touched.type && formik.errors.type}
                </FormHelperText>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <FormControl
                error={
                  formik.touched.subsection_id && formik.errors.subsection_id
                }
                fullWidth
              >
                <Typography variant="formSubHeading">sub-section</Typography>
                <StyledSelect
                  displayEmpty
                  variant="outlined"
                  id="subsection_id"
                  name="subsection_id"
                  value={formik.values.subsection_id}
                  onChange={formik.handleChange}
                  error={
                    formik.touched.subsection_id &&
                    Boolean(formik.errors.subsection_id)
                  }
                >
                  <MenuItem value="all" disabled>
                    select subsection
                  </MenuItem>
                  {subsections?.map((subsection) => (
                    <MenuItem key={subsection.id} value={subsection.id}>
                      {subsection.name}
                    </MenuItem>
                  ))}
                </StyledSelect>
                <FormHelperText variant="error">
                  {formik.touched.subsection_id && formik.errors.subsection_id}
                </FormHelperText>
              </FormControl>
            </Grid>

            <Grid item xs={12} textAlign={"right"}>
              <StyledLoadingButton loading={updateGradeLoading} type="submit">
                Submit
              </StyledLoadingButton>
            </Grid>
          </Grid>
        </form>
      </Box>
    </>
  );
}

export default EditGradeForm;
